import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileText, Plus, Image, File, Trash2, Download, Upload, Loader2, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Documents = () => {
  const { user, isOrganizer, canUploadDocuments } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newDocument, setNewDocument] = useState({
    title: '',
    description: '',
    category: 'photos'
  });
  const [fileData, setFileData] = useState(null);
  const [fileType, setFileType] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Can manage = organizer OR has Documentation committee permission
  const canManage = (isOrganizer && isOrganizer()) || canUploadDocuments();

  const categories = [
    { value: 'photos', label: 'Photos', icon: Image },
    { value: 'minutes', label: 'Meeting Minutes', icon: FileText },
    { value: 'reports', label: 'Financial Reports', icon: File },
    { value: 'member_docs', label: 'Member Documents', icon: FolderOpen },
  ];

  useEffect(() => {
    if (isOrganizer && isOrganizer()) {
      fetchEvents();
    } else {
      if (user?.event_id) {
        setSelectedEventId(user.event_id);
      }
    }
  }, [user, isOrganizer]);

  useEffect(() => {
    if (selectedEventId) {
      fetchDocuments();
    }
  }, [selectedEventId, selectedCategory]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`);
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEventId(response.data[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to load events');
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const categoryParam = selectedCategory !== 'all' ? `&category=${selectedCategory}` : '';
      const response = await axios.get(`${API_URL}/api/event/documents?event_id=${selectedEventId}${categoryParam}`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFileData(reader.result);
      setFileType(file.type || 'application/octet-stream');
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!fileData) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      await axios.post(`${API_URL}/api/event/documents`, {
        title: newDocument.title,
        description: newDocument.description,
        category: newDocument.category,
        event_id: selectedEventId,
        file_data: fileData,
        file_type: fileType
      });
      
      toast.success('Document uploaded successfully');
      setNewDocument({ title: '', description: '', category: 'photos' });
      setFileData(null);
      setFileType('');
      setCreateDialogOpen(false);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/event/documents/${documentId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.icon : File;
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const isImage = (fileType) => fileType?.startsWith('image/');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-maroon" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
              <FileText className="h-8 w-8" />
              Document Library
            </h1>
            <p className="text-gray-600 mt-1">
              {documents.length} documents
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {canManage && events.length > 1 && (
              <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select Event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.event_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {canManage && (
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-maroon hover:bg-maroon-hover text-white rounded-full" data-testid="upload-document-btn">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-maroon">Upload Document</DialogTitle>
                    <DialogDescription>Share files with the reunion members</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleUpload} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        value={newDocument.title}
                        onChange={(e) => setNewDocument({ ...newDocument, title: e.target.value })}
                        placeholder="Document title"
                        required
                        data-testid="document-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select 
                        value={newDocument.category} 
                        onValueChange={(v) => setNewDocument({ ...newDocument, category: v })}
                      >
                        <SelectTrigger data-testid="category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Description (Optional)</Label>
                      <Textarea
                        value={newDocument.description}
                        onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
                        placeholder="Brief description..."
                        data-testid="document-description-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>File</Label>
                      <div 
                        className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-maroon transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {fileData ? (
                          <div>
                            {isImage(fileType) && (
                              <img src={fileData} alt="Preview" className="max-h-32 mx-auto mb-2 rounded" />
                            )}
                            <p className="text-sm text-green-600 font-medium">File selected</p>
                            <p className="text-xs text-gray-500">{fileType}</p>
                          </div>
                        ) : (
                          <div>
                            <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600">Click to select a file</p>
                            <p className="text-xs text-gray-400">Max size: 10MB</p>
                          </div>
                        )}
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileChange}
                        className="hidden"
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                        data-testid="file-input"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        className="bg-maroon hover:bg-maroon-hover text-white" 
                        disabled={uploading}
                        data-testid="submit-document-btn"
                      >
                        {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                        {uploading ? 'Uploading...' : 'Upload'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
          <TabsList className="bg-white p-1 rounded-xl shadow-sm">
            <TabsTrigger value="all" className="rounded-lg" data-testid="tab-all">All</TabsTrigger>
            {categories.map((cat) => (
              <TabsTrigger key={cat.value} value={cat.value} className="rounded-lg" data-testid={`tab-${cat.value}`}>
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Documents Grid */}
        {documents.length === 0 ? (
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600">No documents yet</h3>
              <p className="text-gray-400 mt-1">Upload your first document to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map((doc) => {
              const Icon = getCategoryIcon(doc.category);
              return (
                <Card 
                  key={doc.id}
                  className="border-0 shadow-sm bg-white hover:shadow-lg transition-shadow duration-300 overflow-hidden"
                  data-testid={`document-card-${doc.id}`}
                >
                  {/* Preview */}
                  {isImage(doc.file_type) && doc.file_url && (
                    <div className="aspect-video bg-gray-100 overflow-hidden">
                      <img 
                        src={doc.file_url} 
                        alt={doc.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  {!isImage(doc.file_type) && (
                    <div className="aspect-video bg-gray-100 flex items-center justify-center">
                      <Icon className="h-16 w-16 text-gray-300" />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <Badge variant="outline" className="mb-2 text-xs">
                          {getCategoryLabel(doc.category)}
                        </Badge>
                        <h3 className="font-semibold text-gray-900 truncate">{doc.title}</h3>
                        {doc.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{doc.description}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          By {doc.uploaded_by_name} • {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      {doc.file_url && (
                        <a 
                          href={doc.file_url} 
                          download={doc.title}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1"
                        >
                          <Button variant="outline" className="w-full" data-testid={`download-${doc.id}`}>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        </a>
                      )}
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(doc.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0"
                          data-testid={`delete-document-${doc.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
  );
};

export default Documents;
